import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar } from "../../components/ui/Avatar";
import { Explicit } from "../../components/ui/Explicit";
import { api } from "../../lib/api";
import { clear as clearHistory, forget, recent, remember } from "../../lib/searchHistory";

/**
 * What the search box offers while somebody is typing.
 *
 * ## Why a dropdown rather than only the results page
 *
 * Most searches here are a name somebody already knows - an artist they follow,
 * a track they just heard in a room - and for those the results page is a whole
 * navigation to reach one obvious answer. The dropdown ends the search at the
 * first keystroke that makes it unambiguous.
 *
 * The results page stays for the other kind, where somebody is looking rather
 * than fetching, and "See all results" is the door to it.
 *
 * ## One request, three kinds of thing
 *
 * `type=all` already returns tracks, artists and albums together, capped per
 * kind - so this needs no endpoint of its own. It also means "is there more
 * than we are showing" is answerable exactly, by comparing what came back with
 * what fits, rather than guessing from whether a limit was hit.
 */

interface TrackHit {
  id: number;
  title: string;
  artwork_small: string | null;
  artwork: string | null;
  explicit: boolean;
  owner?: { username: string; display_name: string };
}

interface UserHit {
  id: number;
  username: string;
  display_name: string;
  avatar: string | null;
}

interface AlbumHit {
  id: number;
  title: string;
  artwork_small?: string | null;
  artwork?: string | null;
  owner?: { username: string; display_name: string };
}

interface Results {
  tracks: TrackHit[];
  users: UserHit[];
  collections: AlbumHit[];
  short_query: boolean;
}

/** A flattened hit, so one keyboard cursor can run through every kind. */
interface Row {
  key: string;
  to: string;
  kind: "track" | "artist" | "album";
  title: string;
  subtitle: string;
  image: string | null;
  round: boolean;
  explicit?: boolean;
}

/**
 * How many rows fit before this stops being a shortcut.
 *
 * Long enough that the thing being looked for is usually in it, short enough
 * that it is still a glance. Anything past this is what the results page is
 * for, which is what the link at the bottom says.
 */
const SHOWN = 8;

export function SearchSuggest({
  term,
  open,
  onClose,
  inputRef,
}: {
  term: string;
  open: boolean;
  onClose: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const navigate = useNavigate();
  /**
   * Which row the keyboard is on, or -1 for none.
   *
   * ## Why it does not start at zero
   *
   * It did, and that quietly changed what Enter means. Typing a term and
   * pressing Enter has always gone to the results page; with a row implicitly
   * highlighted it went to whatever happened to be first instead - so searching
   * for a word and hitting Enter opened one arbitrary artist's profile.
   *
   * A cursor nobody has moved is not a selection. Arrow keys create one.
   */
  const [active, setActive] = useState(-1);

  /*
   * Read when the panel opens, not on every render.
   *
   * `localStorage` is synchronous and touching it during render would make
   * every keystroke a disk read. Opening is the only moment the list can have
   * changed since it was last shown - a search committed elsewhere, or another
   * tab - so that is when it is re-read.
   */
  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    if (open) setHistory(recent());
  }, [open]);

  /*
   * Debounced, because this fires on every keystroke.
   *
   * The endpoint is rate limited at three hundred an hour and a fast typist
   * would spend that on one sentence. A quarter of a second is below what reads
   * as lag and above the gap between keystrokes.
   */
  const [debounced, setDebounced] = useState(term);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term), 250);

    return () => window.clearTimeout(timer);
  }, [term]);

  const query = debounced.trim();

  const results = useQuery({
    queryKey: ["search-suggest", query],
    queryFn: () => api.get<Results>(`/search?q=${encodeURIComponent(query)}&type=all`),
    // Two characters is where the server stops calling it a short query, and
    // below that every search matches half the catalogue anyway.
    enabled: open && query.length >= 2,
    staleTime: 30_000,
  });

  /*
   * Artists first, then tracks, then albums.
   *
   * Somebody typing a name usually wants the person; somebody typing a title
   * usually wants the track. Both orders are wrong half the time, and putting
   * artists first loses less - there are far fewer of them, so an artist match
   * on a name is a much stronger signal than a track match on the same string.
   */
  const rows = useMemo<Row[]>(() => {
    const data = results.data;

    if (!data) return [];

    return [
      ...data.users.map((user) => ({
        key: `u${user.id}`,
        to: `/${user.username}`,
        kind: "artist" as const,
        title: user.display_name,
        subtitle: `Artist · @${user.username}`,
        image: user.avatar,
        round: true,
      })),
      ...data.tracks.map((track) => ({
        key: `t${track.id}`,
        to: `/tracks/${track.id}`,
        kind: "track" as const,
        title: track.title,
        subtitle: `Song · ${track.owner?.display_name ?? "Unknown artist"}`,
        image: track.artwork_small ?? track.artwork,
        round: false,
        explicit: track.explicit,
      })),
      ...data.collections.map((album) => ({
        key: `c${album.id}`,
        to: `/albums/${album.id}`,
        kind: "album" as const,
        title: album.title,
        subtitle: `Album · ${album.owner?.display_name ?? "Unknown artist"}`,
        image: album.artwork_small ?? album.artwork ?? null,
        round: false,
      })),
    ];
  }, [results.data]);

  const visible = rows.slice(0, SHOWN);
  /*
   * Known exactly rather than inferred from a limit being hit.
   *
   * The request returns more than fits on purpose, so "there is more" is a
   * comparison instead of a guess - and a search with exactly eight results
   * does not offer a page that adds nothing.
   */
  const more = rows.length > visible.length;

  /*
   * History fills the gap where this used to render nothing.
   *
   * Under two characters the server calls it a short query and there is
   * nothing to suggest, so the panel returned null and focusing an empty
   * search box did nothing at all. That is exactly where "what you looked for
   * last" belongs, and it means the box is useful before a key is pressed.
   */
  const showingHistory = query.length < 2 && history.length > 0;

  const runSearch = (value: string) => {
    // Re-running from history moves it back to the top, the same as typing it
    // would - otherwise the list orders by first search rather than last.
    remember(value);
    navigate(`/search?q=${encodeURIComponent(value)}`);
    onClose();
  };

  /*
   * Going to a hit is a finished search too.
   *
   * This used to record only the form submit and "See all results", on the
   * theory that somebody who types "nova" and clicks Nova Beam never searched
   * for "nova". That was wrong twice over: they did type it, and nothing is
   * ever recorded mid-typing anyway - only at the moment a search ends - so
   * the half-typed fragments it was guarding against could not occur. What it
   * actually produced was an empty history for the most common way to use the
   * box at all.
   */
  const openHit = (to: string) => {
    remember(query);
    navigate(to);
    onClose();
  };

  const drop = (value: string) => {
    forget(value);
    setHistory(recent());
    setActive(-1);
  };

  /* The cursor resets whenever the list changes under it. */
  useEffect(() => setActive(-1), [query]);

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (document.activeElement !== inputRef.current) return;

      const total = showingHistory ? history.length : visible.length + (more ? 1 : 0);

      if (total === 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActive((i) => (i + 1) % total);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        // From nothing, Up goes to the last row rather than to -2.
        setActive((i) => (i <= 0 ? total - 1 : i - 1));
      } else if (event.key === "Enter") {
        /*
         * Enter takes the highlighted row, and the form's own submit takes over
         * when nothing is highlighted - so typing a term and pressing Enter
         * still goes to the results page, which is what it always did.
         */
        if (showingHistory) {
          const term = active >= 0 ? history[active] : undefined;

          if (term !== undefined) {
            event.preventDefault();
            runSearch(term);
          }

          return;
        }

        const row = active >= 0 ? visible[active] : undefined;

        if (row) {
          event.preventDefault();
          openHit(row.to);
        }
        if (more && active === visible.length) {
          event.preventDefault();
          runSearch(query);
        }
      } else if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [open, visible, more, active, navigate, onClose, inputRef, query, showingHistory, history]);

  if (!open) return null;
  if (query.length < 2 && !showingHistory) return null;

  const empty = results.isSuccess && rows.length === 0;

  return (
    <div
      role="listbox"
      aria-label="Search results"
      data-search-suggest
      /*
       * As wide as the field, and never narrower than this.
       *
       * The field shrinks with the viewport - it is `flex-1` between the logo
       * and the nav - and the list following it down to a few hundred pixels
       * would truncate every title. The floor is what the rows actually need;
       * the cap keeps it on screen when the window is smaller than the floor.
       */
      className="absolute left-0 right-0 top-[calc(100%+8px)] z-40 w-[max(100%,min(30rem,calc(100vw-3rem)))] overflow-hidden rounded-[var(--radius-panel)] border border-line-lit bg-raised py-1.5 shadow-2xl"
    >
      {showingHistory && (
        <>
          <div className="flex items-center justify-between px-4 pb-1 pt-2">
            <span className="meta-caps text-faint">Recent searches</span>
            <button
              type="button"
              data-search-history-clear
              // Pointer-down, not click: the input blurs first otherwise, the
              // panel unmounts, and the click lands on whatever replaced it.
              onPointerDown={(event) => {
                event.preventDefault();
                clearHistory();
                setHistory([]);
              }}
              className="meta-caps text-ghost transition-colors hover:text-hot-text"
            >
              Clear
            </button>
          </div>

          {history.map((term, i) => (
            /*
              A row, not a button, because it holds two actions - run this
              search, and forget it - and a button inside a button is invalid
              markup that browsers resolve by ignoring one of them.
            */
            <div
              key={term}
              role="option"
              aria-selected={i === active}
              data-search-history={term}
              onPointerEnter={() => setActive(i)}
              className={`flex w-full items-center gap-3 px-3 py-2 transition-colors ${
                i === active ? "bg-slate" : ""
              }`}
            >
              <button
                type="button"
                tabIndex={-1}
                onPointerDown={(event) => {
                  event.preventDefault();
                  runSearch(term);
                }}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <span
                  aria-hidden
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-slate text-faint"
                >
                  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <circle cx="8" cy="8" r="6" />
                    <path d="M8 4.6V8l2.2 1.6" />
                  </svg>
                </span>
                <span className="truncate text-sm text-ink">{term}</span>
              </button>

              <button
                type="button"
                tabIndex={-1}
                aria-label={`Forget “${term}”`}
                data-search-history-remove={term}
                onPointerDown={(event) => {
                  event.preventDefault();
                  // Or the row underneath runs the search it just removed.
                  event.stopPropagation();
                  drop(term);
                }}
                className="shrink-0 rounded-[var(--radius-pill)] px-2 py-1 font-mono text-[11px] text-ghost transition-colors hover:text-hot-text"
              >
                ✕
              </button>
            </div>
          ))}
        </>
      )}

      {!showingHistory && results.isPending && (
        <p className="px-4 py-3 font-mono text-[11px] text-ghost">searching…</p>
      )}

      {!showingHistory && empty && (
        <p className="px-4 py-3 text-sm text-faint">
          Nothing matching “{query}”
        </p>
      )}

      {!showingHistory && visible.map((row, i) => (
        <button
          key={row.key}
          type="button"
          role="option"
          aria-selected={i === active}
          data-search-hit={row.kind}
          onPointerEnter={() => setActive(i)}
          onPointerDown={(event) => {
            // Or the input blurs first, the dropdown closes, and the click
            // lands on whatever the page put in its place.
            event.preventDefault();
            openHit(row.to);
          }}
          className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
            i === active ? "bg-slate" : ""
          }`}
        >
          {row.kind === "artist" ? (
            <Avatar src={row.image} name={row.title} size={40} />
          ) : row.image ? (
            <img
              src={row.image}
              alt=""
              className="h-10 w-10 shrink-0 rounded-[var(--radius-control)] object-cover"
            />
          ) : (
            <span
              aria-hidden
              className="h-10 w-10 shrink-0 rounded-[var(--radius-control)]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, var(--color-blank) 0 6px, var(--color-blank-alt) 6px 12px)",
              }}
            />
          )}

          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 items-center gap-1.5">
              {row.explicit && <Explicit />}
              <span className="truncate text-sm font-medium text-ink">
                {row.title}
              </span>
            </span>
            <span className="meta block truncate text-faint">{row.subtitle}</span>
          </span>
        </button>
      ))}

      {!showingHistory && more && (
        <button
          type="button"
          role="option"
          aria-selected={active === visible.length}
          data-search-see-all
          onPointerEnter={() => setActive(visible.length)}
          onPointerDown={(event) => {
            event.preventDefault();
            runSearch(query);
          }}
          className={`mt-1 flex w-full items-center justify-between border-t border-line px-4 py-2.5 text-left text-sm transition-colors ${
            active === visible.length ? "bg-slate text-ink" : "text-soft"
          }`}
        >
          See all results
          <span aria-hidden className="font-mono text-[11px] text-faint">
            →
          </span>
        </button>
      )}
    </div>
  );
}
